import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom, Observable, Subscriber } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { WsTypeaheadDirective } from '../../UI-Lib/Typeahead/typeahead.directive';
import { EntityPropertyCharacteristicsService } from '../Entity-Property-Characteristics/entity.property.characteristics.service';
import { DescriptorService } from '../descriptor.service';
import { DescriptorEntityType, EntityDescriptorModelUI, TypeaheadDescriptorDTO } from '../descriptor.model';
import { BrowseCharacteristicsComponent } from './Browse-Characteristics/browseCharacteristics.component';

import { groupBy, map as _map, toArray, sortBy } from 'lodash/fp';
import * as _ from 'lodash';

@Component({
    selector: 'property-characteristics-picker',
    templateUrl: './property.characteristics.picker.component.html',
    imports: [FormsModule, WsTypeaheadDirective],
})
export class PropertyCharacteristicsPickerComponent implements OnInit {
    constructor(
        private readonly _entityDescriptorService: EntityPropertyCharacteristicsService,
        private readonly _descriptorService: DescriptorService,
        private readonly _modalService: WeissmanModalService,
    ) {}

    @Input() entityId: number;
    @Input() entityName: DescriptorEntityType;
    @Input() descriptorsToExclude: EntityDescriptorModelUI[];
    @Input() disabled: boolean = false;
    @Input() isForCompanyOverrides: boolean = false;

    @Output() descriptorsSelected: EventEmitter<Core.DescriptorDTO[]> = new EventEmitter();

    typeaheadLoading: boolean;
    selectedDescriptorName: string;
    dataSource: Observable<TypeaheadDescriptorDTO[]>;
    descriptorCategories: any[];

    async ngOnInit() {
        this.dataSource = new Observable((observer: Subscriber<string>) =>
            observer.next(this.selectedDescriptorName),
        ).pipe(
            mergeMap((token) => {
                return this.searchAvailable({
                    filter: token,
                    descriptorIdsToExclude: _.map(this.descriptorsToExclude, 'descriptorID'),
                    pageSize: 10,
                });
            }),
            map((descriptors: Core.DescriptorDTO[]) => {
                return _.map(descriptors, (x) => {
                    return {
                        ...x,
                        typeaheadField: `${x.categoryName}: ${x.name}`,
                    } as TypeaheadDescriptorDTO;
                });
            }),
        );
    }

    descriptorSelected(descriptor: Core.DescriptorDTO) {
        this.descriptorsSelected.emit([descriptor]);
        this.selectedDescriptorName = '';
    }

    changeTypeaheadLoading(e: boolean): void {
        this.typeaheadLoading = e;
    }

    async openBrowseModal(): Promise<void> {
        const searchModel = {
            filter: '',
            descriptorIdsToExclude: _.map(this.descriptorsToExclude, 'descriptorID'),
            pageSize: null,
        };
        const availableDescriptors = await lastValueFrom(this.searchAvailable(searchModel));
        this.descriptorCategories = _.flow([
            groupBy('categoryID'),
            toArray,
            _map((descriptors: Core.DescriptorDTO[]) => {
                return {
                    name: descriptors[0].categoryName,
                    categoryId: descriptors[0].categoryID,
                    descriptors: descriptors,
                    checkedCount: 0,
                };
            }),
            sortBy('name'),
        ])(availableDescriptors);

        const params = {
            descriptorCategories: this.descriptorCategories,
        };
        const result = await this._modalService.showAsync(BrowseCharacteristicsComponent, params, 'modal-lg');

        if (!result) {
            return;
        }

        this.descriptorsSelected.emit(result);
    }

    searchAvailable(searchModel: Core.EntityDescriptorSearchModel): Observable<Core.DescriptorDTO[]> {
        if (this.entityName == 'company' && this.isForCompanyOverrides) {
            return this._descriptorService.searchAvailableOverrides(this.entityId, searchModel);
        } else {
            return this._entityDescriptorService.searchAvailable(this.entityId, this.entityName, searchModel);
        }
    }
}
